/**
 * The swimmer-plot model of one subject: study days on x (day 0 = first dose), one swim lane
 * per kind of record (study participation, visits, dosing, adverse events, con meds, abnormal
 * labs, deviations, queries), bars for things with a duration and marks for events. Each item
 * carries the profile path that opens its record. Pure, so it is unit tested.
 */
import type { StudyView } from '../dashboards/StudyView.ts';
import { PALETTE } from '../dashboards/charts.ts';
import { STATUS_COLOUR, gradeColour } from './layout.ts';

export type SwimMark = 'bar' | 'dot' | 'ring' | 'diamond' | 'tick';

export interface SwimItem {
  start: number;
  /** Bars only: the last day (inclusive). */
  end?: number;
  mark: SwimMark;
  colour: string;
  label?: string;
  /** Bars: an outline colour (serious events). */
  outline?: string;
  /** The bar runs on past the axis (ongoing) or started before it. */
  arrowRight?: boolean;
  arrowLeft?: boolean;
  /** Profile path after the subject segment. */
  path: string[];
  tooltip: string[];
}

export interface SwimLane {
  title: string;
  /** Sub-rows so that no two items in a row overlap. */
  rows: SwimItem[][];
  count: number;
}

export interface SwimModel {
  day0Date: string;
  day0Label: string;
  startDay: number;
  endDay: number;
  cutDay: number;
  cutDate: string;
  lanes: SwimLane[];
}

const DAY_MS = 86_400_000;

export function dayOf(date: string, day0: string): number {
  return Math.round((Date.parse(date) - Date.parse(day0)) / DAY_MS);
}

/** Greedy packing into rows so no two items in a row overlap (with `gapDays` between). */
export function packRows(items: SwimItem[], gapDays: number, maxRows = 8): SwimItem[][] {
  const rows: { end: number; items: SwimItem[] }[] = [];
  for (const it of [...items].sort((a, b) => a.start - b.start)) {
    const end = it.end ?? it.start;
    const row = rows.find((r) => r.end + gapDays < it.start);
    if (row) {
      row.items.push(it);
      row.end = Math.max(row.end, end);
    } else if (rows.length < maxRows) rows.push({ end, items: [it] });
    else {
      // Out of rows: share the least busy one rather than drop anything.
      const last = rows.reduce((a, b) => (a.end <= b.end ? a : b));
      last.items.push(it);
      last.end = Math.max(last.end, end);
    }
  }
  return rows.map((r) => r.items);
}

const ENDED = new Set(['Completed', 'Discontinued', 'Screen Failed', 'Lost to Follow-up']);

export function swimmerModel(view: StudyView, subjectId: string): SwimModel | undefined {
  const s = view.subject(subjectId);
  if (!s) return undefined;
  const file = view.file;
  const cutDate = file.study.dataCutDate;
  const day0Date = s.firstDoseDate ?? s.randomisationDate ?? s.consentDate;
  const day0Label = s.firstDoseDate
    ? 'first dose'
    : s.randomisationDate
      ? 'randomisation'
      : 'consent';
  const d = (date: string) => dayOf(date, day0Date);
  const order = (a: { visitCode: string }, b: { visitCode: string }) =>
    view.visitIndex(a.visitCode) - view.visitIndex(b.visitCode);
  const visits = file.visits.filter((v) => v.subjectId === s.id).sort(order);
  const cutDay = d(cutDate);
  const first = Math.min(d(s.consentDate), d(s.screeningDate));
  const last = Math.max(cutDay, ...visits.map((v) => d(v.actualDate ?? v.targetDate)));
  const pad = Math.max(5, Math.round((last - first) * 0.03));
  const startDay = first - pad;
  const endDay = last + pad;
  const clip = (a: number, b: number) => ({
    start: Math.max(startDay, Math.min(a, endDay)),
    end: Math.max(startDay, Math.min(Math.max(a, b), endDay)),
    arrowLeft: a < startDay,
    arrowRight: b > endDay,
  });
  const lanes: SwimLane[] = [];

  // Study participation: screening then treatment, ending at the status date when ended.
  const ended = ENDED.has(s.status);
  const endOfStudy = ended ? d(s.discontinuationDate ?? s.statusDate) : cutDay;
  const treat = s.firstDoseDate ? d(s.firstDoseDate) : undefined;
  const study: SwimItem[] = [
    {
      ...clip(d(s.consentDate), treat ?? endOfStudy),
      mark: 'bar',
      colour: PALETTE.grey,
      label: treat !== undefined ? 'Screening' : s.status,
      path: [],
      tooltip: [
        `Consent ${s.consentDate}`,
        treat !== undefined ? `Screening until first dose ${s.firstDoseDate}` : s.status,
      ],
    },
  ];
  if (treat !== undefined)
    study.push({
      ...clip(treat, endOfStudy),
      arrowRight: !ended,
      mark: 'bar',
      colour: STATUS_COLOUR[s.status],
      label: `${s.arm ?? 'Treatment'} · ${s.status}`,
      path: [],
      tooltip: [
        `${s.arm ?? 'Treatment'} from ${s.firstDoseDate}`,
        ended
          ? `${s.status} ${s.discontinuationDate ?? s.statusDate}${s.discontinuationReason ? ` · ${s.discontinuationReason}` : ''}`
          : `${s.status}, ongoing at the data cut`,
      ],
    });
  lanes.push({ title: 'Study', rows: [study], count: study.length });

  // Visits: filled in window, crimson out of window, rings for missed and scheduled.
  const visitItems: SwimItem[] = visits.map((v) => {
    const day = d(v.actualDate ?? v.targetDate);
    const missed = v.status === 'Missed';
    const done = v.status === 'Completed';
    const late = done && (v.timing === 'Late' || v.timing === 'Early');
    return {
      start: Math.max(startDay, Math.min(day, endDay)),
      mark: done ? 'dot' : 'ring',
      colour: missed ? PALETTE.crimson : late ? PALETTE.amber : done ? PALETTE.teal : PALETTE.grey,
      label: v.visitCode,
      path: ['tab:Visits', `visit:${v.id}`],
      tooltip: [
        `${v.visitName} · ${v.actualDate ?? `target ${v.targetDate}`}`,
        `${v.status} · ${v.timing}${v.deviationDays ? ` (${v.deviationDays > 0 ? '+' : ''}${v.deviationDays} d)` : ''}${v.missedReason ? ` · ${v.missedReason}` : ''}`,
        'Open the visit',
      ],
    };
  });
  lanes.push({ title: 'Visits', rows: [visitItems], count: visitItems.length });

  // Dosing records.
  const dosing = file.dosing.filter((x) => x.subjectId === s.id).sort(order);
  const doseItems: SwimItem[] = dosing.map((x) => ({
    start: Math.max(startDay, Math.min(d(x.date), endDay)),
    mark: 'tick',
    colour: !x.administered ? PALETTE.crimson : x.missedDoses > 0 ? PALETTE.amber : PALETTE.teal,
    path: ['tab:Notes', `dosing:${x.id}`],
    tooltip: [
      `Dosing ${x.visitCode} · ${x.date}`,
      x.administered
        ? `${x.dose} ${x.unit}${x.missedDoses ? ` · ${x.missedDoses} doses missed` : ''}`
        : `Not administered${x.reason ? ` · ${x.reason}` : ''}`,
      'Open the dosing record',
    ],
  }));
  lanes.push({ title: 'Dosing', rows: [doseItems], count: doseItems.length });

  // Adverse events: onset to resolution, coloured by grade, serious ones outlined.
  const aes = file.adverseEvents.filter((a) => a.subjectId === s.id);
  const aeItems: SwimItem[] = aes.map((a) => {
    const end = a.resolutionDate ? d(a.resolutionDate) : cutDay;
    return {
      ...clip(d(a.onsetDate), end),
      arrowRight: a.ongoing || clip(d(a.onsetDate), end).arrowRight,
      mark: 'bar',
      colour: a.grade >= 5 ? PALETTE.crimson : gradeColour(a.grade),
      outline: a.serious ? PALETTE.crimson : undefined,
      label: `${a.term} G${a.grade}${a.serious ? ' SAE' : ''}`,
      path: ['tab:Adverse Events', `ae:${a.id}`],
      tooltip: [
        `${a.serious ? 'SAE' : 'AE'} · ${a.term} · grade ${a.grade}`,
        `${a.onsetDate} to ${a.ongoing ? 'ongoing' : (a.resolutionDate ?? '?')} · ${a.relatedness} · ${a.outcome}`,
        'Open the event',
      ],
    };
  });
  lanes.push({ title: 'Adverse events', rows: packRows(aeItems, 2), count: aeItems.length });

  // Concomitant medications.
  const meds = file.conMeds.filter((c) => c.subjectId === s.id);
  const medItems: SwimItem[] = meds.map((c) => {
    const end = c.endDate ? d(c.endDate) : cutDay;
    const span = clip(d(c.startDate), end);
    return {
      ...span,
      arrowRight: c.ongoing || span.arrowRight,
      mark: 'bar',
      colour: PALETTE.navy,
      label: c.drug,
      path: ['tab:Con meds', `conmed:${c.id}`],
      tooltip: [
        `${c.drug} · ${c.dose} ${c.unit} ${c.frequency}`,
        `${c.indication} · ${c.startDate} to ${c.ongoing ? 'ongoing' : (c.endDate ?? '?')}`,
        'Open the medication',
      ],
    };
  });
  lanes.push({ title: 'Con meds', rows: packRows(medItems, 2), count: medItems.length });

  // Abnormal labs (grade 1 and above).
  const labs = file.labs.filter((l) => l.subjectId === s.id && l.grade > 0);
  const labItems: SwimItem[] = labs.map((l) => ({
    start: Math.max(startDay, Math.min(d(l.date), endDay)),
    mark: 'dot',
    colour: gradeColour(l.grade),
    label: l.grade >= 3 ? l.analyte : undefined,
    path: ['tab:Labs', `analyte:${l.analyte}`, `lab:${l.id}`],
    tooltip: [
      `${l.analyte} · grade ${l.grade} · ${l.date}`,
      `${l.value} ${l.unit} (range ${l.low}–${l.high})`,
      'Open the result',
    ],
  }));
  lanes.push({ title: 'Abnormal labs', rows: packRows(labItems, 0, 3), count: labItems.length });

  // Deviations.
  const devs = file.deviations.filter((x) => x.subjectId === s.id);
  const devItems: SwimItem[] = devs.map((x) => ({
    start: Math.max(startDay, Math.min(d(x.date), endDay)),
    mark: 'diamond',
    colour:
      x.classification === 'Major'
        ? PALETTE.crimson
        : x.classification === null
          ? PALETTE.grey
          : PALETTE.amber,
    label: x.category,
    path: ['tab:Deviations', `deviation:${x.id}`],
    tooltip: [
      `${x.classification ?? 'Unclassified'} deviation · ${x.category}`,
      `${x.date} · ${x.description}`,
      'Open the deviation',
    ],
  }));
  lanes.push({ title: 'Deviations', rows: [devItems], count: devItems.length });

  // Queries.
  const queries = view.queries.filter((q) => q.subjectId === s.id);
  const queryItems: SwimItem[] = queries.map((q) => ({
    start: Math.max(startDay, Math.min(d(q.raisedAt.slice(0, 10)), endDay)),
    mark: 'dot',
    colour:
      q.status === 'Open' ? PALETTE.amber : q.status === 'Answered' ? PALETTE.teal : PALETTE.grey,
    path: ['tab:Queries', `query:${q.id}`],
    tooltip: [
      `${q.status} query · ${q.form}${q.field ? ` · ${q.field}` : ''}`,
      q.text.length > 70 ? `${q.text.slice(0, 67)}...` : q.text,
      'Open the query',
    ],
  }));
  lanes.push({ title: 'Queries', rows: packRows(queryItems, 0, 2), count: queryItems.length });

  return { day0Date, day0Label, startDay, endDay, cutDay, cutDate, lanes };
}

/** A tick step (days) that gives at most about ten ticks across the span. */
export function tickStep(span: number): number {
  for (const step of [7, 14, 28, 56, 84, 182, 365, 730]) if (span / step <= 10) return step;
  return 1461;
}
